import { useEffect } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { MessageCircle, Phone } from "lucide-react";
import MeetZentra from "@/pages/MeetZentra";
import Microgreens from "@/pages/Microgreens";
import FaqPage from "@/pages/FaqPage";
import ContactPage from "@/pages/ContactPage";
import ShippingPolicy from "@/pages/ShippingPolicy";
import RefundPolicy from "@/pages/RefundPolicy";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import ThankYou from "@/pages/ThankYou";
import TrackOrder from "@/pages/TrackOrder";
import AdminLogin from "@/pages/AdminLogin";
import AdminOrders from "@/pages/AdminOrders";
import AdminProducts from "@/pages/AdminProducts";
import AdminSettings from "@/pages/AdminSettings";
import { CartProvider } from "@/shop/CartContext";
import { ProductsProvider } from "@/shop/ProductsContext";
import CartDrawer from "@/shop/CartDrawer";
import axios from "axios";
import { API } from "@/shop/api";
import { initPixel, track } from "@/lib/pixel";

const WHATSAPP = process.env.REACT_APP_WHATSAPP_NUMBER || "919003140803";

const FloatButtons = () => {
  const { pathname } = useLocation();
  if (pathname === "/" || pathname === "/meet-zentra") return null;
  return (
    <>
      <a
        href={`tel:+${WHATSAPP}`}
        data-testid="call-float-button"
        aria-label="Call us"
        className="fixed bottom-[5.5rem] right-6 z-[60] flex h-12 w-12 items-center justify-center rounded-full border border-[#7ED321]/50 bg-[#0A0A0A]/80 backdrop-blur-md transition-transform duration-300 hover:scale-110 md:hidden"
      >
        <Phone className="h-5 w-5 text-[#7ED321]" />
      </a>
      <a
        href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Hi Zentra! I have a question about your protein bars.")}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float-button"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-6 right-6 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] shadow-[0_8px_30px_rgba(37,211,102,0.35)] transition-transform duration-300 hover:scale-110"
      >
        <MessageCircle className="h-6 w-6 text-white" fill="white" />
      </a>
    </>
  );
};

const PixelTracker = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    axios.get(`${API}/shop/config`).then((r) => {
      if (r.data.fb_pixel_id) initPixel(r.data.fb_pixel_id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    track("PageView");
  }, [pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (e.target.closest?.('a[href*="wa.me"]')) track("Contact", { content_name: "WhatsApp chat" });
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <ProductsProvider>
        <CartProvider>
        <div className="bg-[#0A0A0A] text-white min-h-screen" data-testid="app-root">
          <div className="grain-overlay" aria-hidden="true" />
          <Routes>
            <Route path="/" element={<MeetZentra />} />
            <Route path="/meet-zentra" element={<MeetZentra />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/microgreens" element={<Microgreens />} />
            <Route path="/products/:slug" element={<ProductDetail />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/thank-you/:orderId" element={<ThankYou />} />
            <Route path="/track" element={<TrackOrder />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="/admin/products" element={<AdminProducts />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/shipping-policy" element={<ShippingPolicy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <PixelTracker />
          <FloatButtons />
          <CartDrawer />
          <Toaster
            theme="dark"
            position="bottom-left"
            toastOptions={{
              style: {
                background: "#121212",
                border: "1px solid rgba(126,211,33,0.35)",
                color: "#fff",
              },
            }}
          />
        </div>
        </CartProvider>
        </ProductsProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
